import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";
import { polishYearsLabel } from "../../../../utils/polishGrammar";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickOne(arr) {
  return arr[randInt(0, arr.length - 1)];
}

function parseVal(v) {
  if (v && typeof v === "object" && Number.isFinite(v.parsed)) return v.parsed;
  return NaN;
}

const NAME_PAIRS = [
  { older: "Basia", younger: "Asia" }, // B, A
  { older: "Tosia", younger: "Zosia" }, // T, Z
  { older: "Kasia", younger: "Ola" }, // K, O
  { older: "Bartek", younger: "Kajtek" }, // B, K
  { older: "Adam", younger: "Olek" }, // A, O
  { older: "Maja", younger: "Piotr" }, // M, P (mieszana)
];

const GENITIVE = {
  Basia: "Basi",
  Asia: "Asi",
  Tosia: "Tosi",
  Zosia: "Zosi",
  Kasia: "Kasi",
  Ola: "Oli",
  Bartek: "Bartka",
  Kajtek: "Kajtka",
  Adam: "Adama",
  Olek: "Olka",
  Maja: "Mai",
  Piotr: "Piotra",
};

export function generateTask() {
  const pair = pickOne(NAME_PAIRS);

  const youngerAge = randInt(6, 22);
  const diff = randInt(1, 9);
  const olderAge = youngerAge + diff;
  const sum = olderAge + youngerAge;

  const symOlder = pair.older[0].toUpperCase();
  const symYounger = pair.younger[0].toUpperCase();

  const variant = Math.random() < 0.5 ? "olderMore" : "youngerLess";
  const yearsLabel = polishYearsLabel(diff);
  const relationText =
    variant === "olderMore"
      ? `${pair.older} ma o ${diff} ${yearsLabel} więcej niż ${pair.younger}.`
      : `${pair.younger} ma o ${diff} ${yearsLabel} mniej niż ${pair.older}.`;

  return {
    type: "TwoNumbersSumDiffTask",
    data: {
      olderName: pair.older,
      youngerName: pair.younger,
      olderAge,
      youngerAge,
      sum,
      diff,
      symOlder,
      symYounger,
      variant,
      relationText,
    },
  };
}

export function renderTask({ data }) {
  const olderGen = GENITIVE[data.olderName] || data.olderName;
  const youngerGen = GENITIVE[data.youngerName] || data.youngerName;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        Suma wieku {olderGen} i {youngerGen} jest równa{" "}
        <InlineMath math={`${data.sum}`} />.
      </p>
      {data.variant ? (
        <p>
          {data.variant === "olderMore" ? data.olderName : data.youngerName} ma o{" "}
          <InlineMath math={`${data.diff}`} /> {polishYearsLabel(data.diff)}{" "}
          {data.variant === "olderMore" ? "więcej" : "mniej"} niż{" "}
          {data.variant === "olderMore" ? data.youngerName : data.olderName}.
        </p>
      ) : (
        <p>{data.relationText}</p>
      )}
      <p>
        Wyznacz ich wiek. Przyjmij oznaczenia:{" "}
        <InlineMath math={`${data.symOlder}`} /> - wiek {olderGen},{" "}
        <InlineMath math={`${data.symYounger}`} /> - wiek {youngerGen}.
      </p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const aVal = userAnswer?.a || "";
  const bVal = userAnswer?.b || "";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <InlineMath math={`${data.symOlder} =`} />
        <FractionInput
          value={aVal}
          onChange={(v) =>
            onChange({
              ...userAnswer,
              a: v,
              symOlder: data.symOlder,
              symYounger: data.symYounger,
            })
          }
          disabled={disabled}
          label={data.symOlder}
          placeholder="np. 14"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={`${data.symYounger} =`} />
        <FractionInput
          value={bVal}
          onChange={(v) =>
            onChange({
              ...userAnswer,
              b: v,
              symOlder: data.symOlder,
              symYounger: data.symYounger,
            })
          }
          disabled={disabled}
          label={data.symYounger}
          placeholder="np. 11"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const ua = parseVal(userAnswer?.a);
  const ub = parseVal(userAnswer?.b);
  if (!Number.isFinite(ua) || !Number.isFinite(ub)) return { correct: false };

  const okA = Math.abs(ua - data.olderAge) < 0.01;
  const okB = Math.abs(ub - data.youngerAge) < 0.01;
  return { correct: okA && okB };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.35 }}>
      <div>
        <InlineMath math={`${data.symOlder} = ${data.olderAge}`} />{" "}
        ({data.olderName})
      </div>
      <div>
        <InlineMath math={`${data.symYounger} = ${data.youngerAge}`} />{" "}
        ({data.youngerName})
      </div>
      <div style={{ marginTop: "0.2rem" }}>
        <strong>Odpowiedź:</strong> {data.olderName} ma {data.olderAge} lat, a{" "}
        {data.youngerName} ma {data.youngerAge} lat.
      </div>
    </div>
  );
}

export function renderUserAnswer(taskOrUserAnswer, maybeUserAnswer) {
  const hasTask =
    maybeUserAnswer !== undefined &&
    taskOrUserAnswer &&
    typeof taskOrUserAnswer === "object" &&
    taskOrUserAnswer.data;
  const data = hasTask ? taskOrUserAnswer.data : null;
  const userAnswer = hasTask ? maybeUserAnswer : taskOrUserAnswer;
  const aRaw = userAnswer?.a?.raw || "";
  const bRaw = userAnswer?.b?.raw || "";
  if (!aRaw && !bRaw) return <i>(brak odpowiedzi)</i>;

  const olderSym = data?.symOlder || userAnswer?.symOlder || "a";
  const youngerSym = data?.symYounger || userAnswer?.symYounger || "b";

  return (
    <div style={{ textAlign: "left", lineHeight: 1.35 }}>
      {aRaw ? (
        <div>
          <InlineMath math={formatLatexPreview(aRaw, olderSym)} />
        </div>
      ) : (
        <i>(brak {olderSym})</i>
      )}
      {bRaw ? (
        <div>
          <InlineMath math={formatLatexPreview(bRaw, youngerSym)} />
        </div>
      ) : (
        <i>(brak {youngerSym})</i>
      )}
    </div>
  );
}

export function renderExplanation({ data }) {
  const {
    sum,
    diff,
    olderAge,
    youngerAge,
    olderName,
    youngerName,
    symOlder,
    symYounger,
  } = data;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1:</strong> Układamy układ równań z treści:
      </p>
      <BlockMath
        math={String.raw`\begin{cases}${symOlder}+${symYounger}=${sum}\\${symOlder}-${symYounger}=${diff}\end{cases}`}
      />

      <p>
        <strong>Krok 2:</strong> Dodajemy równania stronami:
      </p>
      <BlockMath
        math={`(${symOlder}+${symYounger})+(${symOlder}-${symYounger})=${sum}+${diff}`}
      />
      <BlockMath math={`2${symOlder}=${sum + diff}`} />
      <BlockMath math={`${symOlder}=${olderAge}`} />

      <p>
        <strong>Krok 3:</strong> Podstawiamy do pierwszego równania:
      </p>
      <BlockMath math={`${olderAge}+${symYounger}=${sum}`} />
      <BlockMath math={`${symYounger}=${youngerAge}`} />

      <p>
        <strong>Odpowiedź:</strong> {olderName} ma {olderAge} lat, a{" "}
        {youngerName} ma {youngerAge} lat.
      </p>
    </div>
  );
}

